import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Button, Form } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaFileExcel, FaArrowLeft, FaDownload, FaCalendarAlt } from "react-icons/fa";
import axios from "axios";
import "react-toastify/dist/ReactToastify.css";

const API_URL = process.env.REACT_APP_PYTHON_API_URL || "http://localhost:8003";

const ReporteProgramacionMenuCiclico = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const currentUser = JSON.parse(localStorage.getItem("user"));

  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [programaciones, setProgramaciones] = useState([]);
  const [selectedProgramacionId, setSelectedProgramacionId] = useState("");
  const [loadingProgramaciones, setLoadingProgramaciones] = useState(true);

  // Redirect if not authenticated
  useEffect(() => {
    if (!token || !currentUser) {
      navigate("/");
    }
  }, [navigate, token, currentUser]);

  // Cargar lista de programaciones
  useEffect(() => {
    const cargarProgramaciones = async () => {
      if (!token) return;
      
      try {
        setLoadingProgramaciones(true);
        const response = await axios.get(
          `${API_URL}/api/procesos/planificacion-insumos/programacion-menu-ciclico/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setProgramaciones(response.data || []);
      } catch (error) {
        console.error("Error al cargar programaciones:", error);
        toast.error("Error al cargar la lista de programaciones");
      } finally {
        setLoadingProgramaciones(false);
      }
    };

    cargarProgramaciones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo se ejecuta una vez al montar el componente

  const descargarExcel = async () => {
    // Validar que se haya seleccionado una programación
    if (!selectedProgramacionId) {
      toast.error("Por favor seleccione una programación");
      return;
    }

    try {
      setIsGeneratingExcel(true);
      console.log('📊 Generando reporte de programación de menú cíclico...');

      const response = await axios.get(
        `${API_URL}/api/reportes/planificacion-insumos/programacion-menu-ciclico/excel`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            programacion_id: parseInt(selectedProgramacionId),
          },
          responseType: 'blob', // Importante para recibir el archivo
        }
      );

      // Crear blob y descargar
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Generar nombre del archivo
      const fecha = new Date().toISOString().split('T')[0];
      const selectedProgramacion = programaciones.find(prog => prog.id === parseInt(selectedProgramacionId));
      const filename = `reporte_programacion_menu_ciclico_${selectedProgramacion ? selectedProgramacion.id : 'todos'}_${fecha}.xlsx`;

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      console.log('✅ Excel descargado:', filename);
      toast.success("Reporte Excel descargado exitosamente");

    } catch (error) {
      console.error("❌ Error generando Excel:", error);
      const errorMsg = error.response?.data?.detail || "Error al generar el archivo Excel";
      toast.error(errorMsg);
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-PE', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  return (
    <>
      <style>
        {`
          .dh-maintenance-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
            padding: 2rem 0;
          }
          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
          }
          .dh-card-header {
            border-radius: 15px 15px 0 0;
            padding: 1rem 1.5rem;
            border: none;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 10px;
            padding: 8px 16px;
            font-weight: 600;
            font-size: 0.875rem;
            box-shadow: 0 4px 14px rgba(17, 153, 142, 0.3);
            transition: all 0.3s ease;
          }
          .btn-dh-primary:hover {
            background: linear-gradient(135deg, #38ef7d 0%, #2dd4bf 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(17, 153, 142, 0.4);
            color: white;
          }
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
            border: none;
            color: white;
            border-radius: 10px;
            padding: 8px 16px;
            font-weight: 600;
            font-size: 0.875rem;
            box-shadow: 0 4px 14px rgba(107, 114, 128, 0.3);
            transition: all 0.3s ease;
          }
          .btn-dh-secondary:hover {
            background: linear-gradient(135deg, #4b5563 0%, #374151 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(107, 114, 128, 0.4);
            color: white;
          }
        `}
      </style>

      <div className="dh-maintenance-bg">
        <ToastContainer position="top-right" autoClose={3000} />

        <Container>
          <Row className="justify-content-center">
            <Col lg={10} xl={8}>
              <Card className="dh-card">
                <Card.Header className="dh-card-header">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <FaCalendarAlt className="me-3" size={20} />
                      <h5 className="mb-0" style={{ fontSize: '0.95rem', fontWeight: 700 }}>Reporte de Programación de Menú Cíclico</h5>
                    </div>
                    <Button
                      className="btn-dh-secondary"
                      onClick={() => navigate("/reportes")}
                    >
                      <FaArrowLeft className="me-2" />
                      Volver
                    </Button>
                  </div>
                </Card.Header>

                <Card.Body className="p-4">
                  <div className="text-center">
                    <div className="mt-4">
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold" style={{ fontSize: '0.875rem' }}>
                          <FaCalendarAlt className="me-2" />
                          Seleccionar Programación <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          value={selectedProgramacionId}
                          onChange={(e) => setSelectedProgramacionId(e.target.value)}
                          disabled={loadingProgramaciones || isGeneratingExcel}
                          style={{
                            borderRadius: "10px",
                            border: "2px solid #e5e7eb",
                            padding: "8px 12px",
                            fontSize: "0.875rem",
                          }}
                        >
                          <option value="">
                            {loadingProgramaciones ? "Cargando programaciones..." : "Seleccione una programación"}
                          </option>
                          {programaciones
                            .filter((prog) => prog.activo !== false)
                            .map((prog) => (
                              <option key={prog.id} value={prog.id}>
                                {prog.nombre} ({formatDate(prog.fecha_inicio)} - {formatDate(prog.fecha_fin)})
                              </option>
                            ))}
                        </Form.Select>
                        {programaciones.length === 0 && !loadingProgramaciones && (
                          <Form.Text className="text-muted" style={{ fontSize: '0.8rem' }}>
                            No hay programaciones disponibles
                          </Form.Text>
                        )}
                      </Form.Group>

                      <Button
                        className="btn-dh-primary"
                        onClick={descargarExcel}
                        disabled={isGeneratingExcel || !selectedProgramacionId || loadingProgramaciones}
                      >
                        {isGeneratingExcel ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Generando Excel...
                          </>
                        ) : (
                          <>
                            <FaDownload className="me-2" />
                            Descargar Reporte Excel
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default ReporteProgramacionMenuCiclico;

